// Company Documents (Session AFE): the office manager for the customer-facing company
// document library (H&S policy, insurance certificates, RAMS, COSHH assessments, SAFed
// supplementary-test annexes). Everything listed here is shown to EVERY portal customer,
// which is what distinguishes it from the internal IMS Document Library two nav items up.
// Office/admin. Upload is PDF-only; delete is soft (the Drive file survives).
import { useMemo, useState } from "react";
import { api, ApiError } from "../../lib/api";
import type { CompanyDocCategoryKey } from "../../lib/api";
import { DocumentManager, COMPANY_DOC_CATEGORY_OPTIONS, type DocumentManagerAdapter } from "./portalDocumentsUi";
import { ConfirmButton } from "../../components/ConfirmButton";
import { PageHeader } from "../../components/PageHeader";

export function CompanyDocumentsPage() {
  const adapter = useMemo<DocumentManagerAdapter>(
    () => ({
      categories: COMPANY_DOC_CATEGORY_OPTIONS,
      list: () => api.companyDocuments.list(),
      upload: (file, fields) => api.companyDocuments.upload(file, { ...fields, category: fields.category as CompanyDocCategoryKey }),
      patch: (id, body) => api.companyDocuments.patch(id, body),
      remove: (id) => api.companyDocuments.remove(id),
      downloadHref: (id) => api.companyDocuments.downloadHref(id),
    }),
    [],
  );

  // Session AQS, M-3: the office UI restructure audit found this reachable only through Guvnor
  // (trigger_compliance_upload) even though the underlying route (POST /api/admin/compliance-publish)
  // has existed since it was built for the MCP tool. Renders/re-files the H&S/RAMS/insurance PDFs it
  // already generates and republishes the portal library rows -- reload picks up any change.
  const [publishing, setPublishing] = useState(false);
  const [publishNotice, setPublishNotice] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  async function publish() {
    setPublishing(true);
    setPublishNotice(null);
    try {
      const res = await api.admin.compliancePublish();
      setPublishNotice(
        res.failedCount > 0
          ? { tone: "error", text: `Published ${res.publishedCount} of ${res.documents.length}. ${res.failedCount} failed. See the list below.` }
          : { tone: "success", text: `Published ${res.publishedCount} document${res.publishedCount === 1 ? "" : "s"} to the client portals.` },
      );
      setReloadKey((k) => k + 1);
    } catch (err) {
      setPublishNotice({ tone: "error", text: err instanceof ApiError ? err.message : "Could not publish the compliance documents. Please try again." });
    } finally {
      setPublishing(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Company documents"
        subtitle={
          <>
            The company-wide documents every customer can see and download in the Client Portal: H&amp;S policy, insurance
            certificates, RAMS, COSHH assessments and supplementary-test annexes. Site-specific documents are added on each
            site&apos;s own page.
          </>
        }
        className="mb-5"
        actions={
          <ConfirmButton
            onConfirm={publish}
            label="Publish to portals"
            confirmLabel="Confirm?"
            busy={publishing}
            busyLabel="Publishing..."
            className="max-sm:min-h-11 rounded-ele border border-ele-purple-accent px-3 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
            title="Regenerates and re-files the H&S, RAMS and insurance PDFs, and republishes them to every client portal"
          />
        }
      />
      {publishNotice && (
        <p role="status" className={`mb-4 rounded-ele border px-3 py-2 text-sm ${publishNotice.tone === "success" ? "border-ele-green text-ele-strong" : "border-ele-danger text-ele-danger"}`}>
          {publishNotice.text}
        </p>
      )}
      <DocumentManager
        key={reloadKey}
        adapter={adapter}
        emptyText="No company documents yet. Upload the H&S policy and the current insurance certificates to get started."
      />
    </div>
  );
}
